import { Validator } from 'components/utility/validator'
import React, { createContext, forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { useMulControlledInput } from '../../utility/hooks/useMulControlledInput'
import {
  createQuestionnaireRequesterApi,
  getBuildingQuestionnaireGenericFormApi,
  getQuestionnaireFormsApi,
} from '../api/requester'
import { useBuilding } from '../hooks/useBuilding'
import useRequesterSteps from '../hooks/useRequesterSteps'
import { FormatError, FormatForm, FormatFormFile } from '../utilities/requester'
import CompanySelection from './CompanySelection'
import Disclaimer from './Disclaimer'
import RequestInfoFormSection from './FormRequestInformation'
import FormSelection from './FormSelection'
import UploadFormSection from './FormUpload'
import { Header } from './header'
import FormPreviewer from './FormPreviewer'
import PropertySelection from './PropertySelection'
import RequestSubmissionSection from './RequestSubmission'

export const RequesterQuestionnaireContext = createContext()

export const STEP = Object.freeze({
  DISCLAIMER: 'disclaimer',
  PROPERTY: 'property',
  COMPANY: 'company',
  FORM_SELECTION: 'form_selection',
  FORM_UPLOAD: 'form_upload',
  FORM_REQUEST_INFO: 'form_request_info',
  FORM_GENERIC: 'form_generic',
  SUBMISSION: 'submission',
})

const STEPS_DISPLAY_CLOSE_MODAL = [STEP.FORM_REQUEST_INFO, STEP.FORM_GENERIC, STEP.FORM_SELECTION, STEP.FORM_UPLOAD]
const STEPS_EXPAND = [STEP.FORM_REQUEST_INFO, STEP.FORM_SELECTION, STEP.FORM_UPLOAD]
const TITLE_FORM_PREVIEW = {
  [STEP.FORM_UPLOAD]: 'upload a form to preview',
  [STEP.FORM_SELECTION]: 'select a form to preview',
}

const INITIAL_VALUES = {
  building_structure_id: null,
  unit_id: null,
  buyer_name: null,
  buyer_email: null,
  buyer_phone: null,
}

const schema = {
  building_structure_id: new Validator().required(),
  unit_id: new Validator().required(),
  buyer_name: new Validator().required(),
  buyer_email: new Validator().email().required(),
  buyer_phone: new Validator().phoneNumber().required(),
}

export const GENERIC_QUESTIONNAIRE_KEY = 'generic'

const MAX_RETRIES = 3

const CloseContainer = ({ onClose }) => {
  return (
    <div className="request-questionnaire-close-container">
      <div className="dome-d-flex dome-justify-end">
        <div onClick={onClose} className="dome-d-flex dome-align-center dome-gap6 dome-cursor-pointer">
          <i className="fa-regular fa-xmark dome-color-med-grey"></i>
          <span className="dome-p2 dome-text-w500 dome-color-med-grey">close</span>
        </div>
      </div>
    </div>
  )
}

const RequestQuestionnaire = forwardRef((props, ref) => {
  const {
    initialStep = STEP.COMPANY,
    initialBuilding = null,
    onClose = () => {},
    openCloseModal,
    initialRequesterValue = {},
  } = props
  const initialValue = useMemo(() => ({ ...INITIAL_VALUES, ...initialRequesterValue }), [initialRequesterValue])
  const [step, onNextStep, onBackStep, onUpdateStep] = useRequesterSteps(initialStep)
  const [company, setCompany] = useState(null)
  const [formFile, setFormFile] = useState(null)
  const [building, setBuilding] = useBuilding(initialBuilding)
  const [questionnaireType, setQuestionnaireType] = useState(null)
  const formRequest = useMulControlledInput({ initialValue, schema })

  const expand = STEPS_EXPAND.includes(step) || (step === STEP.FORM_GENERIC && !!formFile)

  const handlePropertySelection = useCallback(
    (building) => {
      const enableDisclaimer = building?.org?.enable_disclaimer
      const disclaimerText = building?.org?.disclaimer_text

      onNextStep(enableDisclaimer && disclaimerText ? STEP.DISCLAIMER : STEP.COMPANY)
      setBuilding(building)
    },
    [onNextStep]
  )

  const handleCompanySelection = useCallback(
    async (company, questionnaireType) => {
      setFormFile(null)
      setQuestionnaireType(questionnaireType)
      const isStandardQuestionnaireType = questionnaireType?.key === GENERIC_QUESTIONNAIRE_KEY

      if (isStandardQuestionnaireType) {
        if (building?.org?.enable_generic_preview) {
          try {
            const res = await getBuildingQuestionnaireGenericFormApi({ building_id: building.id })
            if (res?.data?.form) {
              const formData = res.data.form
              const payload = { file: formData.data, standardForm: true }
              if (formData.pdf_url) {
                payload['pdfUrl'] = formData.pdf_url
              }
              if (formData.page_urls.length > 0) {
                payload['pageUrls'] = formData.page_urls
              }
              setFormFile(FormatFormFile(payload))
            }
          } catch (err) {
            console.log(`Failed to request generic form`)
          }
        }
        onNextStep(STEP.FORM_GENERIC)
        return
      }

      // if chosen company has no forms associated, go to upload form step
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const params = { company_id: company.id, page: 1, per_page: 10 }
          const res = await getQuestionnaireFormsApi({ params })
          const hasForms = res?.data?.total_counts > 0
          const nextStep = hasForms ? STEP.FORM_SELECTION : STEP.FORM_UPLOAD
          onNextStep(nextStep)
          return
        } catch (err) {
          console.log(`handleCompanySelection error on attempt ${attempt}:`, err)
          if (attempt === MAX_RETRIES) {
            onNextStep(STEP.FORM_UPLOAD)
            return
          }
        }
      }
      // end of retries
    },
    [building, onNextStep]
  )

  const handleCompanyCreation = useCallback(
    (company, questionnaireType) => {
      onNextStep(STEP.FORM_UPLOAD)
      setCompany(company)
      setQuestionnaireType(questionnaireType)
    },
    [onNextStep]
  )

  const handleResetForm = () => {
    onUpdateStep(STEP.COMPANY)
    formRequest.setValues(initialValue)
    formRequest.setErrors({})
  }

  const handleClose = useCallback(() => {
    if (STEPS_DISPLAY_CLOSE_MODAL.includes(step) || (step === STEP.COMPANY && company)) {
      openCloseModal()
    } else {
      onClose()
    }
  }, [step, company])

  useImperativeHandle(
    ref,
    () => ({
      onClose() {
        handleClose()
      },
    }),
    [handleClose, onClose]
  )

  const handleSubmitForm = useCallback(
    async (e) => {
      e.preventDefault()
      const formValues = FormatForm({ formValues: formRequest.values, company, questionnaireType, building, formFile })
      try {
        const res = await createQuestionnaireRequesterApi(formValues)
        onNextStep(STEP.SUBMISSION)
      } catch (err) {
        formRequest.setErrors(FormatError(err?.response?.data?.error))
      }
    },
    [building, company, questionnaireType, formRequest, formFile, onNextStep]
  )

  const renderFormCollapse = useCallback(() => {
    switch (step) {
      case STEP.PROPERTY:
        return <PropertySelection onSelect={handlePropertySelection} />
      case STEP.COMPANY:
        return (
          <CompanySelection
            org={building?.org}
            companyTypes={building.company_types}
            onSubmit={handleCompanySelection}
            onCreate={handleCompanyCreation}
          />
        )
      case STEP.FORM_GENERIC:
        return (
          <RequestInfoFormSection
            building={building}
            buildingStructures={building?.building_structures}
            company={company}
            {...formRequest}
            onSubmit={handleSubmitForm}
            onBack={onBackStep}
          />
        )
      default:
        return null
    }
  }, [
    step,
    building,
    onBackStep,
    formRequest,
    handlePropertySelection,
    handleCompanySelection,
    handleCompanyCreation,
    handleSubmitForm,
  ])

  const renderFormExpand = useCallback(() => {
    switch (step) {
      case STEP.FORM_SELECTION:
        return <FormSelection />
      case STEP.FORM_UPLOAD:
        return <UploadFormSection />
      case STEP.FORM_REQUEST_INFO:
      case STEP.FORM_GENERIC:
        return (
          <RequestInfoFormSection
            building={building}
            buildingStructures={building?.building_structures}
            company={company}
            {...formRequest}
            onSubmit={handleSubmitForm}
            onBack={onBackStep}
          />
        )
      default:
        return null
    }
  }, [step, building, formRequest, onBackStep, handleSubmitForm])

  return (
    <RequesterQuestionnaireContext.Provider
      value={{ step, onNextStep, onBackStep, onUpdateStep, building, company, setCompany, formFile, setFormFile }}
    >
      <div aria-expanded={expand} className="request-questionnaire request-questionnaire-container dome-d-flex">
        {step === STEP.DISCLAIMER && (
          <Disclaimer
            enableDisclaimer={building?.org?.enable_disclaimer}
            disclaimerText={building?.org?.disclaimer_text}
            onClose={handleClose}
            onSubmit={() => onNextStep(STEP.COMPANY)}
          />
        )}
        {![STEP.SUBMISSION, STEP.DISCLAIMER].includes(step) && (
          <div className="form-collapse">
            <CloseContainer onClose={handleClose} />
            <Header />
            {renderFormCollapse()}
          </div>
        )}
        {step === STEP.SUBMISSION && <RequestSubmissionSection onClose={onClose} onRequest={handleResetForm} />}
        {expand && (
          <div aria-expanded={expand} className="form-expand dome-d-flex">
            <FormPreviewer
              title={TITLE_FORM_PREVIEW[step]}
              pdfLink={formFile?.pdf_url}
              pageLinks={formFile?.page_urls}
            />
            <div className="form-expand-content">
              <CloseContainer onClose={handleClose} />
              <Header />
              {renderFormExpand()}
            </div>
          </div>
        )}
      </div>
    </RequesterQuestionnaireContext.Provider>
  )
})

export default RequestQuestionnaire
